import { useNavigate, useParams } from "react-router-dom";
import { ActionItemsSection } from "../components/meeting/ActionItemsSection";
import { useAuth } from "../contexts/AuthContext";
import { MeetingHeader } from "../components/meeting/MeetingHeader";
import { MeetingNotes } from "../components/meeting/MeetingNotes";
import { Meeting } from "../types/meetings";
import { useToast } from "../components/ui/use-toast";
import { useMeetingNotes } from "../hooks/useMeetingNotes";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meetingsService } from "../services/supabase/meetings";
import React from "react";
import { Button } from "../components/ui/button";

export function MeetingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: meeting, isLoading: isMeetingLoading, error: meetingError } = useQuery({
    queryKey: ['meeting', id, user?.id],
    queryFn: () => {
      if (!id || !user?.id) throw new Error('Missing required parameters');
      return meetingsService.getMeeting(id, user.id);
    },
    enabled: !!id && !!user?.id,
  });

  const { data: meetingNotes, isLoading: isNotesLoading } = useMeetingNotes(id, user?.id);

  const updateMeetingMutation = useMutation({
    mutationFn: async (updates: Partial<Meeting>) => {
      if (!id || !user?.id) throw new Error('Missing required parameters');
      return meetingsService.updateMeeting(id, user.id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast({
        title: "Success",
        description: "Meeting updated successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const deleteMeetingMutation = useMutation({
    mutationFn: async () => {
      if (!id || !user?.id) throw new Error('Missing required parameters');
      return meetingsService.deleteMeeting(id, user.id);
    },
    onSuccess: () => {
      toast({
        title: "Meeting deleted",
        description: "The meeting has been successfully deleted."
      });
      navigate("/app/meetings");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleSave = async (updatedMeeting: Meeting) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to save changes",
        variant: "destructive"
      });
      return;
    }

    await updateMeetingMutation.mutateAsync(updatedMeeting);
  };

  const handleDelete = async () => {
    await deleteMeetingMutation.mutateAsync();
  };

  if (isMeetingLoading || isNotesLoading) {
    return <div>Loading...</div>;
  }

  if (meetingError) {
    return <div>Error: {meetingError.message}</div>;
  }

  if (!meeting) {
    return <div>Meeting not found</div>;
  }

  // Ensure we're working with a single MeetingNotes object
  const notes = Array.isArray(meetingNotes) ? meetingNotes[0] : meetingNotes;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <MeetingHeader
        meeting={meeting}
        onSave={handleSave}
        onDelete={handleDelete}
      >
        <div className="flex gap-2">
          <Button onClick={handleDelete} className="button button-destructive">
            Delete Meeting
          </Button>
          <Button onClick={() => handleSave(meeting)} className="button button-default">
            Save Changes
          </Button>
        </div>
      </MeetingHeader>

      {notes && (
        <MeetingNotes 
          notes={notes}
          meetingId={id}
          userId={user?.id}
          onActionItemChange={() => queryClient.invalidateQueries({ queryKey: ['actionItems', id] })}
          actionItems={meeting?.action_items || []}
        />
      )}
    </div>
  );
}
